/**
 * UndoHandlers
 * Central registry for all undo/redo operation handlers
 */

import { Operation, NodeOperation, UIOperation, DashboardOperation } from '@/shared/types/projectData';
import { StateSetters, OperationHandler } from './types';

// Import all handler registration functions
import { registerUIStateHandlers } from './handlers/uiStateHandlers';
import { registerNodeOperationHandlers } from './handlers/nodeOperationHandlers';
import { registerKanbanHandlers } from './handlers/kanbanHandlers';
import { registerClipboardHandlers } from './handlers/clipboardHandlers';
import { registerFileHandlers } from './handlers/fileHandlers';
import { registerDashboardHandlers } from './handlers/dashboardHandlers';
import { registerEphemeralHandlers } from './handlers/ephemeralHandlers';

// Re-export for backward compatibility
export type { StateSetters, OperationHandler };

function buildReplayOperation(op: NodeOperation): NodeOperation {
  const metadata = op.metadata && typeof op.metadata === 'object'
    ? { ...op.metadata }
    : undefined;

  if (metadata && 'optimisticHistoryEntryId' in metadata) {
    delete (metadata as Record<string, unknown>).optimisticHistoryEntryId;
  }

  const {
    id: _id,
    sessionId: _sessionId,
    sequenceNumber: _sequenceNumber,
    sequence_number: _sequenceNumberLegacy,
    operationId: _operationId,
    ...replayOp
  } = op;

  return {
    ...replayOp,
    timestamp: Date.now(),
    metadata,
    skipHistory: true,
  };
}

/**
 * Central registry for all undo/redo operation handlers
 * Delegates handler registration to specialized modules
 */
export class UndoHandlers {
  private handlers: Map<string, OperationHandler> = new Map();

  constructor(
    private applyOp: (op: NodeOperation) => Promise<void>,
    private setters: StateSetters,
    private applyDashboardOp?: (op: DashboardOperation) => Promise<void>
  ) {
    this.registerHandlers();
  }

  /**
   * Register all operation handlers from specialized modules
   */
  private registerHandlers() {
    const replayApplyOp = async (op: NodeOperation) => {
      const replay = buildReplayOperation(op);
      await this.applyOp(replay);
      if (replay.type === 'delete-tree' && replay.sourceOperationId) op.sourceOperationId = replay.sourceOperationId;
    };

    // Register UI state handlers (selection, expansion, filtering, etc.)
    registerUIStateHandlers(this.handlers, this.setters);

    // Register node CRUD operation handlers (create, update, delete, move, etc.)
    registerNodeOperationHandlers(this.handlers, replayApplyOp);

    // Register Kanban column handlers (add, update, delete, reorder columns)
    registerKanbanHandlers(this.handlers, replayApplyOp);

    // Register clipboard handlers (paste, duplicate node/subtree)
    registerClipboardHandlers(this.handlers, replayApplyOp);

    // Register file attachment handlers (attach, detach, delete, rename)
    registerFileHandlers(this.handlers, replayApplyOp);

    // Register dashboard widget handlers (add, remove, resize, move, config)
    registerDashboardHandlers(this.handlers, this.applyDashboardOp);

    // Register ephemeral/no-op handlers (drag, copy, cut)
    registerEphemeralHandlers(this.handlers);
  }

  /**
   * Execute undo for an operation
   */
  async executeUndo(operation: Operation): Promise<void> {
    const operationType = operation.type === 'ui-state-change'
      ? (operation as UIOperation).details.operationType
      : operation.type;

    if (!operationType) return;

    const handler = this.handlers.get(operationType);
    if (handler) {
      await handler.undo(operation);
    }
  }

  /**
   * Execute redo for an operation
   */
  async executeRedo(operation: Operation): Promise<void> {
    const operationType = operation.type === 'ui-state-change'
      ? (operation as UIOperation).details.operationType
      : operation.type;

    if (!operationType) return;

    const handler = this.handlers.get(operationType);
    if (handler) {
      await handler.redo(operation);
    }
  }

  /**
   * Get list of all supported operation types
   */
  getSupportedOperations(): string[] {
    return Array.from(this.handlers.keys());
  }

  /**
   * Check if an operation type is supported
   */
  isOperationSupported(operationType: string): boolean {
    return this.handlers.has(operationType);
  }
}
